import { useState } from 'react';
import { Link } from 'wouter';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { SafeImage } from '@/components/ui/safe-image';
import { ExternalLink, History } from 'lucide-react';
import type { CampaignPhase, StoreProfileDetail } from '../../types';
import { ComplianceBadge } from '../ComplianceBadge';

interface CampaignsByAisleTabProps {
  profile: StoreProfileDetail;
}

const ALL_AISLES = 'all';
const UNASSIGNED = 'unknown';

export function CampaignsByAisleTab({ profile }: CampaignsByAisleTabProps) {
  const [selectedAisleId, setSelectedAisleId] = useState(profile.aisles[0]?.id ?? ALL_AISLES);
  const [phase, setPhase] = useState<CampaignPhase>('current');
  const [cycleFilter, setCycleFilter] = useState('all');
  const [advertiserFilter, setAdvertiserFilter] = useState('all');
  const [showHistory, setShowHistory] = useState(false);

  const selectedAisle = profile.aisles.find((a) => a.id === selectedAisleId);
  const advertisers = [...new Set(profile.campaigns.map((c) => c.advertiser))];
  const cycles = [...new Set(profile.campaigns.map((c) => c.cycleCode))];
  const unassignedCount = profile.campaigns.filter((c) => c.aisleId === UNASSIGNED).length;

  const matchesAisle = (campaignAisleId: string) => {
    if (selectedAisleId === ALL_AISLES) return true;
    if (selectedAisleId === UNASSIGNED) return campaignAisleId === UNASSIGNED;
    return campaignAisleId === selectedAisleId;
  };

  const aisleCampaigns = profile.campaigns.filter((c) => {
    if (!matchesAisle(c.aisleId)) return false;
    if (c.phase !== phase) return false;
    if (cycleFilter !== 'all' && c.cycleCode !== cycleFilter) return false;
    if (advertiserFilter !== 'all' && c.advertiser !== advertiserFilter) return false;
    return true;
  });

  const historyCampaigns = profile.campaigns
    .filter((c) => matchesAisle(c.aisleId))
    .sort((a, b) => b.installationDate.localeCompare(a.installationDate));

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row gap-6">
        <Card className="lg:w-56 shrink-0">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Aisle Navigator</CardTitle>
          </CardHeader>
          <CardContent className="p-2">
            <div className="grid grid-cols-3 lg:grid-cols-2 gap-1">
              <Button
                variant={selectedAisleId === ALL_AISLES ? 'default' : 'outline'}
                size="sm"
                className="h-auto py-2 flex flex-col"
                onClick={() => setSelectedAisleId(ALL_AISLES)}
              >
                <span className="font-bold">All</span>
                <span className="text-[10px] opacity-80">whole store</span>
              </Button>
              {profile.aisles.map((aisle) => {
                const count = profile.campaigns.filter((c) => c.aisleId === aisle.id && c.phase === 'current').length;
                return (
                  <Button
                    key={aisle.id}
                    variant={selectedAisleId === aisle.id ? 'default' : 'outline'}
                    size="sm"
                    className="h-auto py-2 flex flex-col"
                    onClick={() => setSelectedAisleId(aisle.id)}
                  >
                    <span className="font-bold">{aisle.number}</span>
                    <span className="text-[10px] opacity-80 truncate w-full">{aisle.name.split(' ')[0]}</span>
                    {count > 0 && <Badge variant="secondary" className="text-[10px] mt-1">{count}</Badge>}
                  </Button>
                );
              })}
              {unassignedCount > 0 && (
                <Button
                  variant={selectedAisleId === UNASSIGNED ? 'default' : 'outline'}
                  size="sm"
                  className="h-auto py-2 flex flex-col"
                  onClick={() => setSelectedAisleId(UNASSIGNED)}
                >
                  <span className="font-bold">?</span>
                  <span className="text-[10px] opacity-80">unassigned</span>
                  <Badge variant="secondary" className="text-[10px] mt-1">{unassignedCount}</Badge>
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="flex-1 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              {selectedAisle ? (
                <>
                  <h3 className="text-lg font-semibold">Aisle {selectedAisle.number} — {selectedAisle.name}</h3>
                  <p className="text-sm text-gray-500">{selectedAisle.category} · {selectedAisle.lengthMetres}m</p>
                </>
              ) : selectedAisleId === UNASSIGNED ? (
                <>
                  <h3 className="text-lg font-semibold">Unassigned campaigns</h3>
                  <p className="text-sm text-gray-500">
                    Linked to posting locations that don't map to a canonical aisle yet — assign aisles in the Twin.
                  </p>
                </>
              ) : (
                <>
                  <h3 className="text-lg font-semibold">All campaigns</h3>
                  <p className="text-sm text-gray-500">{profile.campaigns.length} campaign placement{profile.campaigns.length === 1 ? '' : 's'} on record</p>
                </>
              )}
            </div>
            <Button variant="outline" size="sm" onClick={() => setShowHistory(!showHistory)}>
              <History className="h-4 w-4 mr-1" />
              {showHistory ? 'Hide' : 'Show'} timeline
            </Button>
          </div>

          <div className="flex flex-wrap gap-3">
            <Tabs value={phase} onValueChange={(v) => setPhase(v as CampaignPhase)}>
              <TabsList>
                <TabsTrigger value="current">Current</TabsTrigger>
                <TabsTrigger value="previous">Previous</TabsTrigger>
                <TabsTrigger value="planned">Planned</TabsTrigger>
              </TabsList>
            </Tabs>
            <Select value={cycleFilter} onValueChange={setCycleFilter}>
              <SelectTrigger className="w-32"><SelectValue placeholder="Cycle" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All cycles</SelectItem>
                {cycles.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={advertiserFilter} onValueChange={setAdvertiserFilter}>
              <SelectTrigger className="w-44"><SelectValue placeholder="Advertiser" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All advertisers</SelectItem>
                {advertisers.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {aisleCampaigns.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-gray-500">No {phase} campaigns match the selected filters for this aisle.</CardContent></Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {aisleCampaigns.map((campaign) => (
                <Card key={campaign.id} className="overflow-hidden">
                  {campaign.installationImageUrls[0] && (
                    <SafeImage src={campaign.installationImageUrls[0]} alt={campaign.campaignName} className="w-full h-36 object-cover" placeholderType="photo" />
                  )}
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-semibold">{campaign.campaignName}</p>
                        <p className="text-sm text-gray-500">{campaign.advertiser} · {campaign.brand}</p>
                      </div>
                      <ComplianceBadge status={campaign.complianceStatus} />
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Badge variant="outline">{campaign.formatLabel}</Badge>
                      <Badge variant="secondary">{campaign.cycleCode}</Badge>
                    </div>
                    <dl className="text-sm grid grid-cols-2 gap-2">
                      <div><dt className="text-gray-500">Installed</dt><dd>{campaign.installedQuantity} / {campaign.plannedQuantity}</dd></div>
                      <div><dt className="text-gray-500">Install date</dt><dd>{campaign.installationDate}</dd></div>
                      <div><dt className="text-gray-500">Removal due</dt><dd>{campaign.expectedRemovalDate}</dd></div>
                      {campaign.latestImageDate && (
                        <div><dt className="text-gray-500">Latest image</dt><dd>{campaign.latestImageDate}</dd></div>
                      )}
                    </dl>
                    {campaign.issues && campaign.issues.length > 0 && (
                      <div className="text-sm text-amber-700 bg-amber-50 rounded p-2">
                        {campaign.issues.map((issue) => <p key={issue}>⚠ {issue}</p>)}
                      </div>
                    )}
                    {campaign.evidenceRoute && (
                      <Link href={campaign.evidenceRoute}>
                        <Button variant="outline" size="sm" className="w-full">
                          <ExternalLink className="h-3 w-3 mr-1" /> View campaign evidence
                        </Button>
                      </Link>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {showHistory && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {selectedAisle ? `Aisle ${selectedAisle.number}` : selectedAisleId === UNASSIGNED ? 'Unassigned' : 'Store'} — Campaign Timeline
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="relative border-l-2 border-blue-200 ml-3 space-y-4 pl-6">
              {historyCampaigns.map((c) => (
                <div key={c.id} className="relative">
                  <span className="absolute -left-[1.6rem] w-3 h-3 rounded-full bg-blue-500 border-2 border-white" />
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm text-gray-500">{c.installationDate}</span>
                    <Badge variant="outline">{c.cycleCode}</Badge>
                    <Badge variant="secondary">{c.phase}</Badge>
                  </div>
                  <p className="font-medium">{c.campaignName}</p>
                  <p className="text-sm text-gray-600">{c.formatLabel} — {c.brand}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
